import { useEffect, useMemo, useState } from 'react';
import {
  ConfigCard,
  ConfigDraftJsonDrawer,
  ConfigEditorSection,
  ConfigEditorShell,
  ConfigField,
  ConfigHelperText,
  ConfigKeyValueEditor,
  ConfigRepeatableRows,
  ConfigSegmentedControl,
  ConfigSelect,
  ConfigTextarea,
  ConfigTextInput,
  ConfigToggle,
  useConfigDraftEditor,
  type ConfigKeyValueEntry,
  type ConfigSelectOption,
} from '../configEditor';
import { cloneConfigValue } from '../configEditor/model';
import { useVisibleCopy } from '../i18n';
import type { PersonaWorkbenchSources } from './model';

export type PersonaConfigSpec = Record<string, unknown>;

export type PersonaConfigEditorInput = {
  currentDraft: PersonaConfigSpec | null;
  published: PersonaConfigSpec | null;
  current: PersonaConfigSpec | null;
  baseRevision: number;
};

export type PersonaStructuredConfigEditorProps = {
  sources: PersonaWorkbenchSources;
  onSaveDraft: (values: PersonaConfigSpec, baseRevision: number) => Promise<PersonaConfigSpec | void> | PersonaConfigSpec | void;
  onDirtyChange?: (dirty: boolean) => void;
};

type ArtifactKind = 'skills' | 'prompt_modules' | 'mcp_servers';
type ArtifactBinding = Record<string, unknown>;
type ProviderRow = Record<string, unknown>;
type PlatformBindingRow = Record<string, unknown>;
type RuntimeDefaultRow = { platform: string; provider: string; model: string };
type PlatformOverrideRow = { platform: string; display_mode: string; prompt_modules: string[] };
type MCPAuthRuleRow = Record<string, unknown>;

const emptyPersonaConfigSpec: PersonaConfigSpec = {
  agent: { type: 'codex', providers: [], options: {}, platform_defaults: {} },
  skills: [],
  prompt_modules: [],
  mcp_servers: [],
  platforms: [],
  platform_overrides: {},
};

const agentTypeOptions = [
  { value: 'codex', label: 'Codex' },
  { value: 'claudecode', label: 'Claude Code' },
  { value: 'opencode', label: 'OpenCode' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'qoder', label: 'Qoder' },
];

const reasoningOptions = [
  { value: 'inherit', label: 'Inherit' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'X-High' },
];

const displayModeOptions = [
  { value: 'inherit', label: 'Inherit' },
  { value: 'default', label: 'Default' },
  { value: 'concise', label: 'Concise' },
  { value: 'quiet', label: 'Quiet' },
];

const triStateOptions = [
  { value: 'inherit', label: 'Inherit' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

const mcpAuthModeOptions = [
  { value: 'app', label: 'App token' },
  { value: 'user', label: 'User token' },
];

const mcpMissingUserOptions = [
  { value: 'error', label: 'Error' },
  { value: 'fallback', label: 'Fallback to app' },
];

export function PersonaStructuredConfigEditor({ sources, onSaveDraft, onDirtyChange }: PersonaStructuredConfigEditorProps) {
  const input = useMemo(() => buildPersonaConfigEditorInput(sources), [sources]);
  const [draftJsonOpen, setDraftJsonOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const editor = useConfigDraftEditor<PersonaConfigSpec>({
    currentDraft: input.currentDraft,
    published: input.published,
    current: input.current,
    empty: emptyPersonaConfigSpec,
    onSaveDraft: values => onSaveDraft(compactPersonaConfigSpec(values), input.baseRevision),
  });

  useEffect(() => {
    onDirtyChange?.(editor.dirty);
  }, [editor.dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const values = editor.values;
  const agent = asRecord(values.agent);
  const display = asRecord(values.display);
  const style = asRecord(values.style);
  const sessionCleanup = asRecord(values.session_cleanup);
  const rateLimit = asRecord(values.rate_limit);
  const providerOptions = providerSelectOptions(getArray(agent.providers).map(asRecord), stringValue(agent.provider));

  const updateRoot = (updater: (current: PersonaConfigSpec) => PersonaConfigSpec) => editor.updateValues(updater);
  const updateRootField = (field: string, value: unknown) => editor.setField(field, value);
  const updateAgent = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => updateRoot(current => ({ ...current, agent: updater(asRecord(current.agent)) }));
  const updateDisplay = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => updateKnownRecord(updateRoot, 'display', updater);
  const updateStyle = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => updateKnownRecord(updateRoot, 'style', updater);
  const updateSessionCleanup = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => updateKnownRecord(updateRoot, 'session_cleanup', updater);
  const updateRateLimit = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => updateKnownRecord(updateRoot, 'rate_limit', updater);
  const updateAgentOption = (key: string, value: unknown) => updateAgent(current => ({
    ...current,
    options: setOptionalRecordValue(asRecord(current.options), key, value),
  }));

  const saveDraft = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await editor.saveDraft();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ConfigEditorShell
        title="Structured Persona configuration"
        description="Edit Persona-owned runtime, display, platform override, artifact binding, and absorbed project configuration fields without using raw JSON as the primary path."
        source={editor.source}
        dirty={editor.dirty}
        saving={saving}
        labels={{ draftJson: 'View draft JSON + diff', unsaved: 'Unsaved structured changes' }}
        onSaveDraft={() => void saveDraft()}
        onDiscardChanges={editor.discardChanges}
        onOpenDraftJson={() => setDraftJsonOpen(true)}
      >
        <ConfigEditorSection title="Agent runtime" description="Agent, provider, model, reasoning, token budget, and provider catalog controls saved on the Persona draft.">
          <div className="content-grid">
            <ConfigCard title="Runtime identity" description="Select the active agent and provider; model stays editable because provider catalogs may contain custom model IDs.">
              <ConfigField id="persona-agent-type" label="Agent type" required>
                <ConfigSelect
                  id="persona-agent-type"
                  value={stringValue(agent.type)}
                  onChange={type => updateAgent(current => ({ ...current, type }))}
                  options={withCurrentOption(agentTypeOptions, stringValue(agent.type))}
                />
              </ConfigField>
              <ConfigField id="persona-provider" label="Active provider">
                <ConfigSelect
                  id="persona-provider"
                  value={stringValue(agent.provider)}
                  onChange={provider => updateAgent(current => setOptionalRecordValue(current, 'provider', provider))}
                  options={providerOptions}
                  placeholder="Inherit provider"
                />
              </ConfigField>
              <ConfigField id="persona-model" label="Active model">
                <ConfigTextInput id="persona-model" value={stringValue(agent.model)} onChange={model => updateAgent(current => setOptionalRecordValue(current, 'model', model))} placeholder="Inherit provider model" />
              </ConfigField>
            </ConfigCard>

            <ConfigCard title="Reasoning and budgets" description="Structured options cover existing reasoning and token budget fields while preserving unrelated provider options.">
              <ConfigField label="Model reasoning effort">
                <ConfigSegmentedControl
                  label="Model reasoning effort"
                  value={stringValue(asRecord(agent.options).model_reasoning_effort) || 'inherit'}
                  onChange={value => updateAgentOption('model_reasoning_effort', value === 'inherit' ? undefined : value)}
                  options={reasoningOptions}
                />
              </ConfigField>
              <ConfigField label="Reasoning effort">
                <ConfigSegmentedControl
                  label="Reasoning effort"
                  value={stringValue(asRecord(agent.options).reasoning_effort) || 'inherit'}
                  onChange={value => updateAgentOption('reasoning_effort', value === 'inherit' ? undefined : value)}
                  options={reasoningOptions}
                />
              </ConfigField>
              <ConfigField id="persona-token-budget" label="Token budget">
                <ConfigTextInput id="persona-token-budget" value={stringValue(asRecord(agent.options).token_budget)} onChange={value => updateAgentOption('token_budget', parseOptionalNumber(value))} placeholder="Inherit" />
              </ConfigField>
              <ConfigField id="persona-thinking-budget" label="Thinking budget tokens">
                <ConfigTextInput id="persona-thinking-budget" value={stringValue(asRecord(agent.options).thinking_budget_tokens)} onChange={value => updateAgentOption('thinking_budget_tokens', parseOptionalNumber(value))} placeholder="Inherit" />
              </ConfigField>
            </ConfigCard>
          </div>

          <ConfigCard title="Provider catalog" description="Secret refs and env maps are preserved but secret values are not rendered in this editor.">
            <ProviderRows providers={getArray(agent.providers).map(asRecord)} onChange={providers => updateAgent(current => ({ ...current, providers }))} />
          </ConfigCard>
        </ConfigEditorSection>

        <ConfigEditorSection title="Reasoning, display, and Persona style" description="Display mode, debug routing, truncation limits, session cleanup, and persona-style prompt layer controls.">
          <div className="content-grid">
            <ConfigCard title="Display controls">
              <ConfigField label="Display mode">
                <ConfigSegmentedControl
                  label="Display mode"
                  value={stringValue(display.mode) || 'inherit'}
                  onChange={value => updateDisplay(current => setOptionalRecordValue(current, 'mode', value === 'inherit' ? undefined : value))}
                  options={withCurrentOption(displayModeOptions, stringValue(display.mode) || 'inherit')}
                />
              </ConfigField>
              <ConfigField label="Route debug">
                <ConfigSegmentedControl
                  label="Route debug"
                  value={triStateValue(values.display_route_debug)}
                  onChange={value => updateRootField('display_route_debug', value === 'inherit' ? undefined : value === 'enabled')}
                  options={triStateOptions}
                />
              </ConfigField>
              <ConfigField id="persona-thinking-max" label="Thinking max length">
                <ConfigTextInput id="persona-thinking-max" value={stringValue(display.thinking_max_len)} onChange={value => updateDisplay(current => setOptionalRecordValue(current, 'thinking_max_len', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
              <ConfigField id="persona-tool-max" label="Tool max length">
                <ConfigTextInput id="persona-tool-max" value={stringValue(display.tool_max_len)} onChange={value => updateDisplay(current => setOptionalRecordValue(current, 'tool_max_len', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
            </ConfigCard>

            <ConfigCard title="Persona style">
              <ConfigField id="persona-style-voice" label="Voice">
                <ConfigTextInput id="persona-style-voice" value={stringValue(style.voice)} onChange={voice => updateStyle(current => setOptionalRecordValue(current, 'voice', voice))} placeholder="Concise, rigorous, friendly…" />
              </ConfigField>
              <ConfigField label="Preferences">
                <StringListEditor items={getArray(style.preferences).map(stringValue)} onChange={preferences => updateStyle(current => setOptionalRecordValue(current, 'preferences', preferences))} addLabel="Add preference" itemLabel="Preference" />
              </ConfigField>
              <ConfigField id="persona-custom-instructions" label="Custom instructions">
                <ConfigTextarea id="persona-custom-instructions" value={stringValue(style.custom_instructions)} onChange={customInstructions => updateStyle(current => setOptionalRecordValue(current, 'custom_instructions', customInstructions))} rows={5} placeholder="Persona-specific prompt layer" />
              </ConfigField>
            </ConfigCard>
          </div>

          <div className="content-grid">
            <ConfigCard title="Session cleanup">
              <ConfigField label="Cleanup enabled">
                <ConfigSegmentedControl
                  label="Cleanup enabled"
                  value={triStateValue(sessionCleanup.enabled)}
                  onChange={value => updateSessionCleanup(current => setOptionalRecordValue(current, 'enabled', value === 'inherit' ? undefined : value === 'enabled'))}
                  options={triStateOptions}
                />
              </ConfigField>
              <ConfigField id="persona-inactive-days" label="Inactive days">
                <ConfigTextInput id="persona-inactive-days" value={stringValue(sessionCleanup.inactive_days)} onChange={value => updateSessionCleanup(current => setOptionalRecordValue(current, 'inactive_days', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
              <ConfigField id="persona-keep-latest" label="Keep latest sessions">
                <ConfigTextInput id="persona-keep-latest" value={stringValue(sessionCleanup.keep_latest)} onChange={value => updateSessionCleanup(current => setOptionalRecordValue(current, 'keep_latest', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
            </ConfigCard>

            <ConfigCard title="Command and rate controls">
              <ConfigField label="Disabled commands">
                <StringListEditor items={getArray(values.disabled_commands).map(stringValue)} onChange={items => updateRootField('disabled_commands', items)} addLabel="Add disabled command" itemLabel="Command" />
              </ConfigField>
              <ConfigField label="Disabled built-in prompts">
                <StringListEditor items={getArray(values.disabled_builtin_prompts).map(stringValue)} onChange={items => updateRootField('disabled_builtin_prompts', items)} addLabel="Add built-in prompt" itemLabel="Built-in prompt" />
              </ConfigField>
              <ConfigField id="persona-rate-max" label="Rate max messages">
                <ConfigTextInput id="persona-rate-max" value={stringValue(rateLimit.max_messages)} onChange={value => updateRateLimit(current => setOptionalRecordValue(current, 'max_messages', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
              <ConfigField id="persona-rate-window" label="Rate window seconds">
                <ConfigTextInput id="persona-rate-window" value={stringValue(rateLimit.window_secs)} onChange={value => updateRateLimit(current => setOptionalRecordValue(current, 'window_secs', parseOptionalNumber(value)))} placeholder="Inherit" />
              </ConfigField>
              <ConfigField id="persona-log-file" label="Log file">
                <ConfigTextInput id="persona-log-file" value={stringValue(values.log_file)} onChange={logFile => updateRootField('log_file', logFile || undefined)} placeholder="Inherit" />
              </ConfigField>
            </ConfigCard>
          </div>
        </ConfigEditorSection>

        <ConfigEditorSection title="Platform overrides" description="Persona-level platform bindings, provider/model defaults, display overrides, and platform-scoped prompt module lists.">
          <ConfigCard title="Platform bindings">
            <PlatformBindingRows platforms={getArray(values.platforms).map(asRecord)} onChange={platforms => updateRootField('platforms', platforms)} />
          </ConfigCard>
          <ConfigCard title="Runtime defaults by platform">
            <RuntimeDefaultRows rows={runtimeDefaultsToRows(asRecord(agent.platform_defaults))} providers={providerOptions} onChange={rows => updateAgent(current => setOptionalRecordValue(current, 'platform_defaults', runtimeDefaultRowsToRecord(rows)))} />
          </ConfigCard>
          <ConfigCard title="Display and prompt overrides by platform">
            <PlatformOverrideRows rows={platformOverridesToRows(asRecord(values.platform_overrides))} onChange={rows => updateRootField('platform_overrides', platformOverrideRowsToRecord(rows))} />
          </ConfigCard>
        </ConfigEditorSection>

        <ConfigEditorSection title="Prompt, Skill, and MCP bindings" description="Persona remains the binding center; Prompt, Skill, and MCP resource pages continue to show usage evidence instead of owning assignment state.">
          <div className="content-grid">
            <ConfigCard title="Skill bindings">
              <ArtifactBindingRows kind="skills" values={getArray(values.skills).map(asRecord)} availableArtifacts={asRecord(asRecord(sources.config).available_artifacts)} onChange={items => updateRootField('skills', items)} />
            </ConfigCard>
            <ConfigCard title="Prompt bindings">
              <ArtifactBindingRows kind="prompt_modules" values={getArray(values.prompt_modules).map(asRecord)} availableArtifacts={asRecord(asRecord(sources.config).available_artifacts)} onChange={items => updateRootField('prompt_modules', items)} />
            </ConfigCard>
          </div>
          <ConfigCard title="MCP bindings">
            <ArtifactBindingRows kind="mcp_servers" values={getArray(values.mcp_servers).map(asRecord)} availableArtifacts={asRecord(asRecord(sources.config).available_artifacts)} onChange={items => updateRootField('mcp_servers', items)} />
          </ConfigCard>
        </ConfigEditorSection>

        <ConfigEditorSection title="Absorbed persona/project config" description="Role bindings and persona-level Linear MCP auth policies migrated from legacy project configuration stay editable here.">
          <div className="content-grid">
            <ConfigCard title="Role bindings">
              <RoleBindingRows values={getArray(values.roles).map(asRecord)} onChange={roles => updateRootField('roles', roles)} />
            </ConfigCard>
            <ConfigCard title="Linear MCP auth policy">
              <LinearMCPAuthEditor value={values.linear_mcp_auth} onChange={linearMCPAuth => updateRootField('linear_mcp_auth', linearMCPAuth)} />
            </ConfigCard>
          </div>
          {getArray(values.secret_refs).length ? (
            <ConfigCard title="Secret references preserved" description="Secret references exist on this Persona draft, but secret values stay outside this structured editor and are never rendered.">
              <ConfigHelperText>{getArray(values.secret_refs).length} persona secret reference(s) are preserved in the draft payload.</ConfigHelperText>
            </ConfigCard>
          ) : null}
        </ConfigEditorSection>

        {saveError ? <p className="config-error" role="alert">{saveError}</p> : null}
      </ConfigEditorShell>

      <ConfigDraftJsonDrawer
        open={draftJsonOpen}
        onClose={() => setDraftJsonOpen(false)}
        draft={compactPersonaConfigSpec(values)}
        published={editor.publishedValue}
        title="Draft JSON"
        diffTitle="Draft vs published diff"
        redactionOptions={{ redactPromptContent: true }}
      />
    </>
  );
}

export function buildPersonaConfigEditorInput(sources: PersonaWorkbenchSources): PersonaConfigEditorInput {
  const config = asRecord(sources.config);
  const draft = asRecord(config.draft);
  const diff = asRecord(sources.diff);
  const hasDraftSpec = Object.prototype.hasOwnProperty.call(draft, 'spec');
  const hasPublishedSpec = Object.prototype.hasOwnProperty.call(diff, 'published_spec') || Object.prototype.hasOwnProperty.call(asRecord(config.latest_version), 'spec');
  return {
    currentDraft: hasDraftSpec ? normalizePersonaConfigSpec(draft.spec) : null,
    published: hasPublishedSpec ? normalizePersonaConfigSpec(diff.published_spec ?? asRecord(config.latest_version).spec) : null,
    current: hasDraftSpec ? normalizePersonaConfigSpec(draft.spec) : null,
    baseRevision: numberValue(draft.revision ?? asRecord(sources.status).draft_revision ?? asRecord(asRecord(sources.status).draft).revision),
  };
}

export function normalizePersonaConfigSpec(value: unknown): PersonaConfigSpec {
  const source = cloneConfigValue(asRecord(value));
  const agent = asRecord(source.agent);
  const normalized: PersonaConfigSpec = {
    ...source,
    agent: {
      ...agent,
      type: stringValue(agent.type) || 'codex',
      provider: stringValue(agent.provider),
      model: stringValue(agent.model),
      options: asRecord(agent.options),
      providers: getArray(agent.providers).map(provider => normalizeProvider(provider)),
      platform_defaults: normalizePlatformDefaults(agent.platform_defaults),
    },
    skills: getArray(source.skills).map(item => normalizeArtifactBinding(item)),
    prompt_modules: getArray(source.prompt_modules).map(item => normalizeArtifactBinding(item)),
    mcp_servers: getArray(source.mcp_servers).map(item => normalizeArtifactBinding(item)),
    platforms: getArray(source.platforms).map(item => normalizePlatformBinding(item)),
    platform_overrides: normalizePlatformOverrides(source.platform_overrides),
  };

  if (source.display !== undefined) normalized.display = asRecord(source.display);
  if (source.style !== undefined) normalized.style = normalizeStyle(source.style);
  if (source.session_cleanup !== undefined) normalized.session_cleanup = asRecord(source.session_cleanup);
  if (source.rate_limit !== undefined) normalized.rate_limit = asRecord(source.rate_limit);
  if (source.linear_mcp_auth !== undefined) normalized.linear_mcp_auth = normalizeLinearMCPAuth(source.linear_mcp_auth);
  if (source.roles !== undefined) normalized.roles = getArray(source.roles).map(asRecord);
  return normalized;
}

export function compactPersonaConfigSpec(value: unknown): PersonaConfigSpec {
  const source = cloneConfigValue(asRecord(value));
  const compacted: PersonaConfigSpec = { ...source };
  compacted.agent = compactAgent(asRecord(source.agent));
  setCompactedArray(compacted, 'skills', getArray(source.skills).map(item => compactArtifactBinding(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedArray(compacted, 'prompt_modules', getArray(source.prompt_modules).map(item => compactArtifactBinding(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedArray(compacted, 'mcp_servers', getArray(source.mcp_servers).map(item => compactArtifactBinding(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedArray(compacted, 'platforms', getArray(source.platforms).map(item => compactPlatformBinding(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedRecord(compacted, 'platform_overrides', compactPlatformOverrides(asRecord(source.platform_overrides)));
  setCompactedRecord(compacted, 'display', compactKnownRecord(asRecord(source.display)));
  setCompactedRecord(compacted, 'style', compactStyle(asRecord(source.style)));
  setCompactedRecord(compacted, 'session_cleanup', compactKnownRecord(asRecord(source.session_cleanup)));
  setCompactedRecord(compacted, 'rate_limit', compactKnownRecord(asRecord(source.rate_limit)));
  setCompactedArray(compacted, 'disabled_commands', cleanStringList(source.disabled_commands));
  setCompactedArray(compacted, 'disabled_builtin_prompts', cleanStringList(source.disabled_builtin_prompts));
  setCompactedArray(compacted, 'roles', getArray(source.roles).map(item => compactKnownRecord(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedRecord(compacted, 'linear_mcp_auth', compactLinearMCPAuth(asRecord(source.linear_mcp_auth)));
  if (!stringValue(compacted.log_file)) delete compacted.log_file;
  return compacted;
}

function ProviderRows({ providers, onChange }: { providers: ProviderRow[]; onChange: (providers: ProviderRow[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={providers}
      onChange={onChange}
      createItem={() => ({ name: '', model: '' })}
      addLabel="Add provider"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="content-grid">
          <ConfigField label={`Provider ${index + 1} name`}>
            <ConfigTextInput aria-label={`Provider ${index + 1} name`} value={stringValue(item.name)} onChange={name => updateItem(setOptionalRecordValue(item, 'name', name))} />
          </ConfigField>
          <ConfigField label="Default model">
            <ConfigTextInput aria-label={`Provider ${index + 1} model`} value={stringValue(item.model)} onChange={model => updateItem(setOptionalRecordValue(item, 'model', model))} />
          </ConfigField>
          <ConfigField label="Base URL">
            <ConfigTextInput aria-label={`Provider ${index + 1} base URL`} value={stringValue(item.base_url)} onChange={baseURL => updateItem(setOptionalRecordValue(item, 'base_url', baseURL))} />
          </ConfigField>
          <ConfigField label="Thinking">
            <ConfigSegmentedControl
              label={`Provider ${index + 1} thinking`}
              value={stringValue(item.thinking) || 'inherit'}
              onChange={thinking => updateItem(setOptionalRecordValue(item, 'thinking', thinking === 'inherit' ? undefined : thinking))}
              options={reasoningOptions}
            />
          </ConfigField>
          <div className="drawer-actions">
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove provider')}</button>
          </div>
          {getArray(item.secret_refs).length ? <ConfigHelperText>{getArray(item.secret_refs).length} secret ref(s) preserved; values hidden.</ConfigHelperText> : null}
        </div>
      )}
    />
  );
}

function RuntimeDefaultRows({ rows, providers, onChange }: { rows: RuntimeDefaultRow[]; providers: ConfigSelectOption[]; onChange: (rows: RuntimeDefaultRow[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={rows}
      onChange={onChange}
      createItem={() => ({ platform: '', provider: '', model: '' })}
      addLabel="Add platform runtime default"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="config-key-value-row">
          <ConfigTextInput aria-label={`Platform ${index + 1}`} value={item.platform} onChange={platform => updateItem({ ...item, platform })} placeholder="platform" />
          <ConfigSelect aria-label={`Platform ${index + 1} provider`} value={item.provider} onChange={provider => updateItem({ ...item, provider })} options={withCurrentOption(providers, item.provider)} placeholder="Inherit provider" />
          <ConfigTextInput aria-label={`Platform ${index + 1} model`} value={item.model} onChange={model => updateItem({ ...item, model })} placeholder="model" />
          <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
        </div>
      )}
    />
  );
}

function PlatformOverrideRows({ rows, onChange }: { rows: PlatformOverrideRow[]; onChange: (rows: PlatformOverrideRow[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={rows}
      onChange={onChange}
      createItem={() => ({ platform: '', display_mode: '', prompt_modules: [] })}
      addLabel="Add platform override"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="page-stack">
          <div className="config-key-value-row">
            <ConfigTextInput aria-label={`Override platform ${index + 1}`} value={item.platform} onChange={platform => updateItem({ ...item, platform })} placeholder="platform" />
            <ConfigSelect aria-label={`Override display mode ${index + 1}`} value={item.display_mode || 'inherit'} onChange={displayMode => updateItem({ ...item, display_mode: displayMode === 'inherit' ? '' : displayMode })} options={displayModeOptions} />
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
          </div>
          <StringListEditor items={item.prompt_modules} onChange={promptModules => updateItem({ ...item, prompt_modules: promptModules })} addLabel="Add platform prompt" itemLabel="Platform prompt" />
        </div>
      )}
    />
  );
}

function PlatformBindingRows({ platforms, onChange }: { platforms: PlatformBindingRow[]; onChange: (platforms: PlatformBindingRow[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={platforms}
      onChange={onChange}
      createItem={() => ({ type: '', options: {} })}
      addLabel="Add platform binding"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="page-stack">
          <div className="config-key-value-row">
            <ConfigTextInput aria-label={`Platform binding ${index + 1}`} value={stringValue(item.type)} onChange={type => updateItem(setOptionalRecordValue(item, 'type', type))} placeholder="linear, slack, lark…" />
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
          </div>
          <ConfigField label="Platform options">
            <ConfigKeyValueEditor entries={recordToEntries(asRecord(item.options))} onChange={entries => updateItem(setOptionalRecordValue(item, 'options', entriesToRecord(entries)))} addLabel="Add option" />
          </ConfigField>
          {getArray(item.secret_refs).length ? <ConfigHelperText>{getArray(item.secret_refs).length} platform secret ref(s) preserved; values hidden.</ConfigHelperText> : null}
        </div>
      )}
    />
  );
}

function ArtifactBindingRows({ kind, values, availableArtifacts, onChange }: { kind: ArtifactKind; values: ArtifactBinding[]; availableArtifacts: Record<string, unknown>; onChange: (values: ArtifactBinding[]) => void }) {
  const ui = useVisibleCopy();
  const options = artifactSelectOptions(availableArtifacts, kind, values.map(item => stringValue(item.artifact_id)));
  const label = kind === 'skills' ? 'skill' : kind === 'prompt_modules' ? 'prompt module' : 'MCP server';
  return (
    <ConfigRepeatableRows
      items={values}
      onChange={onChange}
      createItem={() => ({ artifact_id: '', version: 0, enabled: true })}
      addLabel={`Add ${label} binding`}
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="content-grid">
          <ConfigField label={`${label} artifact`}>
            <ConfigSelect aria-label={`${label} artifact ${index + 1}`} value={stringValue(item.artifact_id)} onChange={artifactID => updateItem(setOptionalRecordValue(item, 'artifact_id', artifactID))} options={options} placeholder={`Select ${label}`} />
          </ConfigField>
          <ConfigField label="Version">
            <ConfigTextInput aria-label={`${label} version ${index + 1}`} value={stringValue(item.version)} onChange={version => updateItem(setOptionalRecordValue(item, 'version', parseOptionalNumber(version)))} placeholder="latest/published version" />
          </ConfigField>
          <ConfigField label="Enabled">
            <ConfigToggle checked={item.enabled !== false && item.enabled !== 'false'} onChange={enabled => updateItem({ ...item, enabled })} label={`${label} enabled ${index + 1}`} />
          </ConfigField>
          <ConfigField label="Platform scope">
            <ConfigTextInput aria-label={`${label} platform ${index + 1}`} value={stringValue(item.platform)} onChange={platform => updateItem(setOptionalRecordValue(item, 'platform', platform))} placeholder="All platforms" />
          </ConfigField>
          <div className="drawer-actions">
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove binding')}</button>
          </div>
        </div>
      )}
    />
  );
}

function RoleBindingRows({ values, onChange }: { values: Record<string, unknown>[]; onChange: (values: Record<string, unknown>[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={values}
      onChange={onChange}
      createItem={() => ({ name: '', description: '', skill_tier: '' })}
      addLabel="Add role binding"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="content-grid">
          <ConfigField label="Role name">
            <ConfigTextInput aria-label={`Role name ${index + 1}`} value={stringValue(item.name)} onChange={name => updateItem(setOptionalRecordValue(item, 'name', name))} />
          </ConfigField>
          <ConfigField label="Description">
            <ConfigTextInput aria-label={`Role description ${index + 1}`} value={stringValue(item.description)} onChange={description => updateItem(setOptionalRecordValue(item, 'description', description))} />
          </ConfigField>
          <ConfigField label="Skill tier">
            <ConfigTextInput aria-label={`Role skill tier ${index + 1}`} value={stringValue(item.skill_tier)} onChange={skillTier => updateItem(setOptionalRecordValue(item, 'skill_tier', skillTier))} />
          </ConfigField>
          <div className="drawer-actions">
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove role')}</button>
          </div>
        </div>
      )}
    />
  );
}

function LinearMCPAuthEditor({ value, onChange }: { value: unknown; onChange: (value: unknown) => void }) {
  const enabled = isPlainRecord(value);
  const auth = asRecord(value);
  const updateAuth = (updater: (current: Record<string, unknown>) => Record<string, unknown>) => onChange(updater(auth));
  return (
    <div className="page-stack">
      <ConfigField label="Policy enabled">
        <ConfigToggle checked={enabled} onChange={checked => onChange(checked ? { default_mode: 'app', on_missing_user: 'error', rules: [] } : undefined)} label="Linear MCP auth policy enabled" />
      </ConfigField>
      {enabled ? (
        <>
          <ConfigField label="Default auth mode">
            <ConfigSegmentedControl label="Default auth mode" value={stringValue(auth.default_mode) || 'app'} onChange={mode => updateAuth(current => ({ ...current, default_mode: mode }))} options={withCurrentOption(mcpAuthModeOptions, stringValue(auth.default_mode) || 'app')} />
          </ConfigField>
          <ConfigField label="Missing user token behavior">
            <ConfigSegmentedControl label="Missing user token behavior" value={stringValue(auth.on_missing_user) || 'error'} onChange={mode => updateAuth(current => ({ ...current, on_missing_user: mode }))} options={withCurrentOption(mcpMissingUserOptions, stringValue(auth.on_missing_user) || 'error')} />
          </ConfigField>
          <ConfigField label="Agent mention names">
            <StringListEditor items={getArray(auth.agent_names).map(stringValue)} onChange={agentNames => updateAuth(current => setOptionalRecordValue(current, 'agent_names', agentNames))} addLabel="Add agent name" itemLabel="Agent name" />
          </ConfigField>
          <ConfigField label="Auth rules">
            <MCPAuthRuleRows rules={getArray(auth.rules).map(asRecord)} onChange={rules => updateAuth(current => setOptionalRecordValue(current, 'rules', rules))} />
          </ConfigField>
        </>
      ) : null}
    </div>
  );
}

function MCPAuthRuleRows({ rules, onChange }: { rules: MCPAuthRuleRow[]; onChange: (rules: MCPAuthRuleRow[]) => void }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={rules}
      onChange={onChange}
      createItem={() => ({ tool: '', mode: 'user' })}
      addLabel="Add auth rule"
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="page-stack">
          <div className="config-key-value-row">
            <ConfigTextInput aria-label={`MCP auth tool ${index + 1}`} value={stringValue(item.tool)} onChange={tool => updateItem(setOptionalRecordValue(item, 'tool', tool))} placeholder="tool name" />
            <ConfigSelect aria-label={`MCP auth mode ${index + 1}`} value={stringValue(item.mode) || 'user'} onChange={mode => updateItem(setOptionalRecordValue(item, 'mode', mode))} options={withCurrentOption(mcpAuthModeOptions, stringValue(item.mode) || 'user')} />
            <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
          </div>
          <div className="action-row">
            <ConfigToggle checked={booleanValue(item.top_level_only)} onChange={checked => updateItem(setOptionalRecordValue(item, 'top_level_only', checked || undefined))} label={`Top-level only ${index + 1}`} onLabel="Top-level only" offLabel="Any level" />
            <ConfigToggle checked={booleanValue(item.reply_only)} onChange={checked => updateItem(setOptionalRecordValue(item, 'reply_only', checked || undefined))} label={`Reply only ${index + 1}`} onLabel="Reply only" offLabel="Any thread" />
            <ConfigToggle checked={booleanValue(item.requires_agent_mention)} onChange={checked => updateItem(setOptionalRecordValue(item, 'requires_agent_mention', checked || undefined))} label={`Requires agent mention ${index + 1}`} onLabel="Mention required" offLabel="No mention required" />
          </div>
        </div>
      )}
    />
  );
}

function StringListEditor({ items, onChange, addLabel, itemLabel }: { items: string[]; onChange: (items: string[]) => void; addLabel: string; itemLabel: string }) {
  const ui = useVisibleCopy();

  return (
    <ConfigRepeatableRows
      items={items}
      onChange={onChange}
      createItem={() => ''}
      addLabel={addLabel}
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="config-key-value-row">
          <ConfigTextInput aria-label={`${itemLabel} ${index + 1}`} value={item} onChange={updateItem} placeholder={itemLabel} />
          <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
        </div>
      )}
    />
  );
}

function updateKnownRecord(updateRoot: (updater: (current: PersonaConfigSpec) => PersonaConfigSpec) => void, key: string, updater: (current: Record<string, unknown>) => Record<string, unknown>) {
  updateRoot(current => ({ ...current, [key]: updater(asRecord(current[key])) }));
}

function normalizeProvider(value: unknown): ProviderRow {
  const provider = asRecord(value);
  return {
    ...provider,
    name: stringValue(provider.name),
    base_url: stringValue(provider.base_url),
    model: stringValue(provider.model),
    thinking: stringValue(provider.thinking),
    env: asRecord(provider.env),
    secret_refs: getArray(provider.secret_refs).map(asRecord),
  };
}

function normalizeArtifactBinding(value: unknown): ArtifactBinding {
  const binding = asRecord(value);
  return {
    ...binding,
    artifact_id: stringValue(binding.artifact_id ?? binding.id ?? binding.name),
    version: numberValue(binding.version),
    platform: stringValue(binding.platform),
    ...(binding.enabled === undefined ? {} : { enabled: booleanValue(binding.enabled) }),
  };
}

function normalizePlatformBinding(value: unknown): PlatformBindingRow {
  const binding = asRecord(value);
  return {
    ...binding,
    type: stringValue(binding.type ?? binding.name),
    options: asRecord(binding.options),
    secret_refs: getArray(binding.secret_refs).map(asRecord),
  };
}

function normalizeStyle(value: unknown) {
  const style = asRecord(value);
  return {
    ...style,
    voice: stringValue(style.voice),
    preferences: getArray(style.preferences).map(stringValue),
    custom_instructions: stringValue(style.custom_instructions),
  };
}

function normalizePlatformDefaults(value: unknown) {
  return Object.fromEntries(Object.entries(asRecord(value)).map(([platform, current]) => [platform, compactKnownRecord(asRecord(current))]));
}

function normalizePlatformOverrides(value: unknown) {
  return Object.fromEntries(Object.entries(asRecord(value)).map(([platform, current]) => {
    const override = asRecord(current);
    return [platform, {
      ...override,
      display_mode: stringValue(override.display_mode),
      prompt_modules: getArray(override.prompt_modules).map(stringValue),
    }];
  }));
}

function normalizeLinearMCPAuth(value: unknown) {
  const auth = asRecord(value);
  return {
    ...auth,
    default_mode: stringValue(auth.default_mode) || 'app',
    on_missing_user: stringValue(auth.on_missing_user) || 'error',
    agent_names: getArray(auth.agent_names).map(stringValue),
    rules: getArray(auth.rules).map(rule => ({ ...asRecord(rule), tool: stringValue(asRecord(rule).tool), mode: stringValue(asRecord(rule).mode) || 'user' })),
  };
}

function compactAgent(agent: Record<string, unknown>) {
  const compacted = compactKnownRecord(agent);
  compacted.type = stringValue(agent.type) || 'codex';
  setCompactedArray(compacted, 'providers', getArray(agent.providers).map(item => compactProvider(asRecord(item))).filter(hasMeaningfulValues));
  setCompactedRecord(compacted, 'options', compactKnownRecord(asRecord(agent.options)));
  setCompactedRecord(compacted, 'platform_defaults', Object.fromEntries(runtimeDefaultsToRows(asRecord(agent.platform_defaults))
    .filter(row => row.platform)
    .map(row => [row.platform, compactKnownRecord({ provider: row.provider, model: row.model })])
    .filter(([, defaults]) => hasMeaningfulValues(defaults as Record<string, unknown>))));
  return compacted;
}

function compactProvider(provider: Record<string, unknown>) {
  const compacted = compactKnownRecord(provider);
  setCompactedRecord(compacted, 'env', compactKnownRecord(asRecord(provider.env)));
  setCompactedArray(compacted, 'secret_refs', getArray(provider.secret_refs).map(asRecord).filter(hasMeaningfulValues));
  return compacted;
}

function compactArtifactBinding(binding: Record<string, unknown>) {
  const compacted = compactKnownRecord(binding);
  compacted.artifact_id = stringValue(binding.artifact_id);
  if (!compacted.artifact_id) return {};
  if (numberValue(binding.version) > 0) compacted.version = numberValue(binding.version);
  else delete compacted.version;
  if (binding.enabled === undefined) delete compacted.enabled;
  else compacted.enabled = booleanValue(binding.enabled);
  return compacted;
}

function compactPlatformBinding(binding: Record<string, unknown>) {
  const compacted = compactKnownRecord(binding);
  setCompactedRecord(compacted, 'options', compactKnownRecord(asRecord(binding.options)));
  setCompactedArray(compacted, 'secret_refs', getArray(binding.secret_refs).map(asRecord).filter(hasMeaningfulValues));
  return compacted;
}

function compactPlatformOverrides(value: Record<string, unknown>) {
  return Object.fromEntries(platformOverridesToRows(value)
    .filter(row => row.platform)
    .map(row => [row.platform, compactKnownRecord({ display_mode: row.display_mode, prompt_modules: cleanStringList(row.prompt_modules) })])
    .filter(([, override]) => hasMeaningfulValues(override as Record<string, unknown>)));
}

function compactStyle(style: Record<string, unknown>) {
  const compacted = compactKnownRecord(style);
  setCompactedArray(compacted, 'preferences', cleanStringList(style.preferences));
  return compacted;
}

function compactLinearMCPAuth(auth: Record<string, unknown>) {
  const compacted = compactKnownRecord(auth);
  if (!hasMeaningfulValues(compacted)) return {};
  compacted.default_mode = stringValue(auth.default_mode) || 'app';
  compacted.on_missing_user = stringValue(auth.on_missing_user) || 'error';
  setCompactedArray(compacted, 'agent_names', cleanStringList(auth.agent_names));
  setCompactedArray(compacted, 'rules', getArray(auth.rules).map(rule => compactKnownRecord(asRecord(rule))).filter(hasMeaningfulValues));
  return compacted;
}

function compactKnownRecord(record: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => !isEmptyConfigValue(value)));
}

function setCompactedArray(record: Record<string, unknown>, key: string, value: unknown[]) {
  if (value.length) record[key] = value;
  else delete record[key];
}

function setCompactedRecord(record: Record<string, unknown>, key: string, value: Record<string, unknown>) {
  if (hasMeaningfulValues(value)) record[key] = value;
  else delete record[key];
}

function setOptionalRecordValue(record: Record<string, unknown>, key: string, value: unknown): Record<string, unknown> {
  const next = { ...record };
  if (isEmptyConfigValue(value)) delete next[key];
  else next[key] = value;
  return next;
}

function runtimeDefaultsToRows(defaults: Record<string, unknown>): RuntimeDefaultRow[] {
  return Object.entries(defaults)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([platform, value]) => ({ platform, provider: stringValue(asRecord(value).provider), model: stringValue(asRecord(value).model) }));
}

function runtimeDefaultRowsToRecord(rows: RuntimeDefaultRow[]) {
  return Object.fromEntries(rows
    .filter(row => row.platform.trim())
    .map(row => [row.platform.trim(), compactKnownRecord({ provider: row.provider, model: row.model })])
    .filter(([, value]) => hasMeaningfulValues(value as Record<string, unknown>)));
}

function platformOverridesToRows(overrides: Record<string, unknown>): PlatformOverrideRow[] {
  return Object.entries(overrides)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([platform, value]) => ({
      platform,
      display_mode: stringValue(asRecord(value).display_mode),
      prompt_modules: getArray(asRecord(value).prompt_modules).map(stringValue),
    }));
}

function platformOverrideRowsToRecord(rows: PlatformOverrideRow[]) {
  return Object.fromEntries(rows
    .filter(row => row.platform.trim())
    .map(row => [row.platform.trim(), compactKnownRecord({ display_mode: row.display_mode, prompt_modules: cleanStringList(row.prompt_modules) })])
    .filter(([, value]) => hasMeaningfulValues(value as Record<string, unknown>)));
}

function artifactSelectOptions(availableArtifacts: Record<string, unknown>, kind: ArtifactKind, currentValues: string[]): ConfigSelectOption[] {
  const keys = kind === 'skills'
    ? ['skills', 'config:skills']
    : kind === 'prompt_modules'
      ? ['prompt_modules', 'config:prompt_modules']
      : ['mcp_servers', 'mcp_definitions', 'config:mcp_servers', 'config:mcp_definitions'];
  const records = keys.flatMap(key => getArray(availableArtifacts[key]).map(asRecord));
  const options = records.map(record => {
    const value = stringValue(record.id ?? record.artifact_id ?? record.name);
    const displayName = stringValue(record.display_name ?? record.name ?? value);
    const version = numberValue(record.published_version);
    return { value, label: version ? `${displayName} · v${version}` : displayName };
  }).filter(option => option.value);
  return withCurrentOptions(dedupeOptions(options), currentValues);
}

function providerSelectOptions(providers: Record<string, unknown>[], currentProvider: string): ConfigSelectOption[] {
  const options = providers.map(provider => stringValue(provider.name)).filter(Boolean).map(name => ({ value: name, label: name }));
  return withCurrentOption(dedupeOptions(options), currentProvider);
}

function withCurrentOption(options: ConfigSelectOption[], currentValue: string) {
  if (!currentValue || options.some(option => option.value === currentValue)) return options;
  return [{ value: currentValue, label: currentValue }, ...options];
}

function withCurrentOptions(options: ConfigSelectOption[], currentValues: string[]) {
  return currentValues.reduce((currentOptions, value) => withCurrentOption(currentOptions, value), options);
}

function dedupeOptions(options: ConfigSelectOption[]) {
  const seen = new Set<string>();
  return options.filter(option => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}

function recordToEntries(record: Record<string, unknown>): ConfigKeyValueEntry[] {
  return Object.entries(record)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => ({ key, value: entryValueText(value) }));
}

function entriesToRecord(entries: ConfigKeyValueEntry[]) {
  return Object.fromEntries(entries.filter(entry => entry.key.trim()).map(entry => [entry.key.trim(), parseEntryValue(entry.value)]));
}

function entryValueText(value: unknown) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function parseEntryValue(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return value;
    }
  }
  return value;
}

function parseOptionalNumber(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : value;
}

function triStateValue(value: unknown) {
  if (value === true || value === 'true') return 'enabled';
  if (value === false || value === 'false') return 'disabled';
  return 'inherit';
}

function cleanStringList(value: unknown) {
  return getArray(value).map(stringValue).map(item => item.trim()).filter(Boolean);
}

function hasMeaningfulValues(record: Record<string, unknown>) {
  return Object.values(record).some(value => !isEmptyConfigValue(value));
}

function isEmptyConfigValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainRecord(value)) return Object.keys(value).length === 0;
  return false;
}

function asRecord(value: unknown): Record<string, unknown> {
  return isPlainRecord(value) ? value : {};
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function booleanValue(value: unknown) {
  return value === true || value === 'true';
}
